import { useTranslation } from "react-i18next";
import {
  Autocomplete,
  Button,
  Chip,
  Divider,
  FormControl,
  InputBase,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { ListCheckBox } from "../../../generals/ListCheckBox";
import { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { useSendNotifications } from "../../../../data/notifications/hooks";

const SendPushNotifications = ({
  formValues,
  handleInputChange,
  userList,
  fundsList,
}) => {
  const { t } = useTranslation("global");
  const [checked, setChecked] = useState([userList]);

  const { clubFilter, message, fundFilter, languageFilter } = formValues;
  const clubsList = [...new Set(userList.map((user) => user.club))];

  const { error, response, handleSendNotifications } = useSendNotifications();

  const sendPush = async () => {
    await handleSendNotifications(
      checked.map((value) => value.id),
      message
    );
  };

  useEffect(() => {
    if (error) {
      Swal.fire({
        confirmButtonColor: "#001B60",
        text: t("sendNotifications.error"),
        icon: "error",
      });
    }
    if (response) {
      Swal.fire({
        confirmButtonColor: "#001B60",
        text: t("sendNotifications.success"),
        icon: "success",
      });
    }
  }, [error, response, t]);

  const filteredUsers = useMemo(() => {
    let filtered = [...userList];

    if (fundFilter[0] !== "Sin filtro de fondo") {
      filtered = filtered.filter((user) =>
        user.investmentsFundsList.some((fund) => fundFilter.includes(fund))
      );
    }

    if (languageFilter === "Español") {
      filtered = filtered.filter((user) => user.language === "ES");
    } else if (languageFilter === "Inglés") {
      filtered = filtered.filter((user) => user.language === "EN");
    }

    if (clubFilter[0] !== "Todos los clubs") {
      filtered = filtered.filter((user) => clubFilter.includes(user.club));
    }

    return filtered;
  }, [userList, languageFilter, clubFilter, fundFilter]);

  useEffect(() => {
    setChecked(filteredUsers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languageFilter, clubFilter, fundFilter]);

  const onChangeSelect = (event) => {
    const selectedValues = event.target.value;
    const currentSelectedIsAll =
      selectedValues[selectedValues.length - 1] === "Todos los clubs";
    const fistSelectedIsAll = selectedValues[0] === "Todos los clubs";

    if (currentSelectedIsAll) {
      handleInputChange({
        target: { name: "clubFilter", value: ["Todos los clubs"] },
      });
    } else if (fistSelectedIsAll) {
      handleInputChange({
        target: {
          name: "clubFilter",
          value: [selectedValues[selectedValues.length - 1]],
        },
      });
    } else {
      handleInputChange({
        target: { name: "clubFilter", value: selectedValues },
      });
    }
  };

  const onChangeSelectFund = (event) => {
    const selectedValues = event.target.value;
    const currentSelectedIsAll =
      selectedValues[selectedValues.length - 1] === "Sin filtro de fondo";
    const fistSelectedIsAll = selectedValues[0] === "Sin filtro de fondo";

    if (currentSelectedIsAll) {
      handleInputChange({
        target: { name: "fundFilter", value: ["Sin filtro de fondo"] },
      });
    } else if (fistSelectedIsAll) {
      handleInputChange({
        target: {
          name: "fundFilter",
          value: [selectedValues[selectedValues.length - 1]],
        },
      });
    } else {
      handleInputChange({
        target: { name: "fundFilter", value: selectedValues },
      });
    }
  };

  return (
    <Stack>
      <Typography color="primary" variant="h6">
        {t("sendNotifications.title")}
      </Typography>
      <Divider sx={{ marginBottom: "25px", marginTop: "10px" }} />
      <Stack gap={3}>
        <Stack>
          <Typography variant="overline" color="secondary">
            {t("sendNotifications.message")}
          </Typography>
          <InputBase
            fullWidth
            rows={5}
            multiline
            name="message"
            size="small"
            variant="standard"
            onChange={handleInputChange}
            value={message}
          />
        </Stack>
        <Stack gap={2} direction="row">
          <FormControl sx={{ width: "100%" }}>
            <Select
              multiple
              size="small"
              value={fundFilter}
              name="fundFilter"
              onChange={onChangeSelectFund}
              renderValue={(selected) => (
                <div>
                  {selected?.map((value) => (
                    <Chip key={value} label={value} sx={{ marginRight: 1 }} />
                  ))}
                </div>
              )}
            >
              {[
                ...fundsList.map((fund) => fund.name),
                "Sin filtro de fondo",
              ].map((fund) => (
                <MenuItem key={fund} value={fund}>
                  {fund}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        <Stack gap={2} direction="row">
          <FormControl sx={{ width: "100%" }}>
            <Select
              multiple
              size="small"
              value={clubFilter}
              name="clubFilter"
              onChange={onChangeSelect}
              renderValue={(selected) => (
                <div>
                  {selected?.map((value) => (
                    <Chip key={value} label={value} sx={{ marginRight: 1 }} />
                  ))}
                </div>
              )}
            >
              {[...clubsList, "Todos los clubs"].map((club) => (
                <MenuItem key={club} value={club}>
                  {club}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        <Stack gap={2} direction="row">
          <Autocomplete
            disablePortal
            options={["Español", "Inglés", "Todos los idiomas"]}
            size="small"
            renderInput={(params) => (
              <TextField {...params} variant="outlined" name="languageFilter" />
            )}
            value={languageFilter}
            onSelect={handleInputChange}
            fullWidth
          />
        </Stack>
        <ListCheckBox
          listText={filteredUsers}
          setChecked={setChecked}
          checked={checked}
        />
        <Stack alignItems="flex-end">
          <Tooltip
            title={t("sendNotifications.emptyMessage")}
            open={message === ""}
          >
            <span>
              <Button
                onClick={sendPush}
                variant="contained"
                width="250px"
                disabled={message === ""}
              >
                {t("sendNotifications.button")}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default SendPushNotifications;
